import {PayloadAction, createSlice} from "@reduxjs/toolkit";
import {Asset, Tenant, User} from "../../model";
import {Sensor} from "@core/model/Sensor";

export type colorScheme = "light" | "dark" | "system";

const getInitialColorScheme = (): colorScheme => {
  const stored = localStorage.getItem("mui-mode") as colorScheme;
  if (stored) {
    return stored;
  }

  return "system";
};

export type AppState = {
  tenant?: Tenant;
  drawerOpen: boolean;
  drawerIndex?: number;
  colorScheme: colorScheme;
  settingsOpen: boolean;
  users?: User[];
  assets?: Asset[];
  sensors?: Sensor[];
};

const initialState: AppState = {
  tenant: undefined,
  drawerIndex: undefined,
  settingsOpen: false,
  drawerOpen: window.innerWidth >= 1200,
  colorScheme: getInitialColorScheme(),
};

/**
 * Restituisce tutti gli asset di tipo SENSOR presenti nell'albero,
 * inclusi quelli nei sotto-livelli (children).
 *
 * @param roots Array di asset radice da cui partire
 * @returns Asset[] contenente solo i sensori
 */
const getSensors = (roots: Asset[]): Sensor[] => {
  const sensors: Sensor[] = [];

  const traverse = (node: Asset): void => {
    if (node.type === "SENSOR_SCALAR" || node.type === "SENSOR_MULTIVALUE") {
      sensors.push(node as Sensor);
    }
    // ricorsione sui figli
    node.children.forEach(traverse);
  };

  // avvia la traversata da ogni radice
  roots.forEach(traverse);

  return sensors;
};

export const appSlice = createSlice({
  name: "app",
  initialState: initialState,
  reducers: {
    setTenant: (state: AppState, action: PayloadAction<Tenant | undefined>) => {
      state.tenant = action.payload;
    },
    clearApp: (state: AppState) => {
      state.tenant = undefined;
      state.colorScheme = initialState.colorScheme;
    },
    setColorScheme: (state: AppState, action: PayloadAction<colorScheme>) => {
      state.colorScheme = action.payload;
      localStorage.setItem("mui-mode", action.payload);
    },
    setDrawerIndex: (state: AppState, action: PayloadAction<number>) => {
      state.drawerIndex = action.payload;
    },
    setDrawerOpen: (state: AppState, action: PayloadAction<boolean>) => {
      state.drawerOpen = action.payload;
    },
    setSettingsOpen: (state: AppState, action: PayloadAction<boolean>) => {
      state.settingsOpen = action.payload;
    },
    setUsers: (state: AppState, action: PayloadAction<User[]>) => {
      state.users = action.payload;
    },
    setAssets: (state: AppState, action: PayloadAction<Asset[]>) => {
      state.assets = action.payload;
      state.sensors = getSensors(action.payload);
    },
  },
});

export const {
  setTenant,
  setDrawerOpen,
  setDrawerIndex,
  setColorScheme,
  clearApp,
  setSettingsOpen,
  setUsers,
  setAssets,
} = appSlice.actions;

export default appSlice;
